import { useAdjustTrackingEnabled } from '@/hooks/adjust';
import { useAppsFlyerTrackingEnabled } from '@/hooks/apps-flyer';
import React, { FC, useCallback } from 'react';
import configs from 'react-native-config';
import styled from 'styled-components/native';

import adjustSDK from '@webtrader/core/adjust';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useSignUpForm } from '@webtrader/core/hooks/form/sign-up';
import { useTranslation } from '@webtrader/core/i18n';
import { useCompanyFBSDKTracking } from '@webtrader/core/services/company';
import { useSignUp } from '@webtrader/core/services/sign-up';
import { isValidEmail } from '@webtrader/core/validators';

import { AlertManager } from '@/components/Alert/Manager';
import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import ValidationError from '@/components/FormValidationError';
import TextInput from '@/components/TextInput';
import { ALL_MOBILE_ROUTES, navigate } from '@/utils/navigation';
import { openInAppBrowser } from '@/utils/web';

import AcknowledgementMessage from './Acknowledgement';
import CountriesField from './Countries';
import { Form, FormField } from './Styles';
import AccountTypeField from './account-type';
import { trackAppFlyer, trackFBEvents } from './apps-flyer';
import SignUpEmailVerification from './email-verification';

const AuthSignUpForm: FC = () => {
  const { t } = useTranslation();
  const appsFlyerTrackingEnabled = useAppsFlyerTrackingEnabled();
  const adjustTrackingEnabled = useAdjustTrackingEnabled();
  const fbsdkEnabled = useCompanyFBSDKTracking(configs.ENVIRONMENT);
  const onSubmitFail = (message: string) => {
    AlertManager().alert({
      message
    });
  };

  const onSubmitSuccess = useCallback(
    (redirectUrl: string, message: string, title: string, email: string) => {
      if (appsFlyerTrackingEnabled) {
        trackAppFlyer(email);
      }
      if (adjustTrackingEnabled) {
        adjustSDK.trackUserRegistration();
      }
      if (fbsdkEnabled) {
        trackFBEvents(email);
      }

      AlertManager().alert({
        title,
        message,
        onAfterCancel: () => {
          openInAppBrowser(redirectUrl);
          navigate(ALL_MOBILE_ROUTES.AUTH.LOGIN);
        }
      });
    },
    [adjustTrackingEnabled, appsFlyerTrackingEnabled, fbsdkEnabled]
  );

  const {
    values,
    onSubmitForm,
    onCheckTnc,
    onEmailChange,
    onCountryChange,
    onPasswordChange,
    onCustomerTypeChange,
    onOtpChange,
    showCountryValidation,
    showEmailValidation,
    showOtpValidation,
    showPasswordValidation,
    showAcknowledgementValidation,
    isDisableSubmit
  } = useSignUpForm({
    onSubmitFail,
    onSubmitSuccess
  });

  const { email, countryId, otp, password, tncAcknowledged, customerType } = values;

  const { submitting } = useSignUp();

  return (
    <Form>
      <FormField>
        <AccountTypeField value={customerType} disabled={submitting} onSelect={onCustomerTypeChange} />
      </FormField>
      <FormField>
        <CountriesField
          value={countryId}
          onSelect={onCountryChange}
          disabled={submitting}
          customerType={customerType}
          showValidation={showCountryValidation}
        />
      </FormField>
      <FormField>
        <TextInput
          label={t('Email')}
          placeholder={t('Enter email')}
          value={email}
          keyboardType="email-address"
          onChangeText={onEmailChange}
          editable={!submitting}
          testID={EDataTestId.SIGN_UP_EMAIL_INPUT}
        />
        <ValidationError
          showValidation={showEmailValidation}
          message={email && !isValidEmail(email) ? t('Email format invalid') : t('Email is required')}
        />
      </FormField>
      <FormField>
        <SignUpEmailVerification
          otp={otp}
          onOtpChange={onOtpChange}
          submitting={submitting}
          showValidation={showOtpValidation}
          email={email}
        />
      </FormField>
      <FormField>
        <TextInput
          label={t('Password')}
          value={password}
          placeholder={t('Enter password')}
          secureTextEntry={true}
          onChangeText={onPasswordChange}
          editable={!submitting}
          testID={EDataTestId.SIGN_UP_PASSWORD_INPUT}
        />
        <ValidationError
          message={
            password
              ? t('Password must be 6-20 characters long, with at least 1 uppercase letter and 1 number')
              : t('Password is required')
          }
          showValidation={showPasswordValidation}
        />
      </FormField>
      <Checkbox
        checked={tncAcknowledged}
        onCheck={onCheckTnc}
        disabled={submitting}
        testID={tncAcknowledged ? EDataTestId.SIGN_UP_ACKNOWLEDGE_CHECKED : EDataTestId.SIGN_UP_ACKNOWLEDGE_UNCHECKED}
      >
        <AcknowledgementMessage />
      </Checkbox>
      <ValidationError
        showValidation={showAcknowledgementValidation}
        message={t('Please review and accept the Terms and Conditions')}
      />
      <FormActions>
        <Button
          loading={submitting}
          onPress={onSubmitForm}
          testID={EDataTestId.SIGN_UP_PROCEED_KYC_BUTTON}
          disabled={isDisableSubmit}
        >
          {t('CREATE ACCOUNT')}
        </Button>
      </FormActions>
    </Form>
  );
};

export default AuthSignUpForm;

const FormActions = styled.View`
  padding-top: ${dynamicSize.convertSize(14)}px;
`;
